"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { ProjectClaimsList, type ProjectClaimFilters } from "./list"
import axiosInstance from "../../../libs/axios"
import { useDebounce } from "../../../hooks/useDebounce"

const ProjectClaim: React.FC = () => {
  const [claims, setClaims] = useState([])
  const [pagination, setPagination] = useState({
    total_count: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  })
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounce(searchTerm, 400)
  const [advancedFilters, setAdvancedFilters] = useState<ProjectClaimFilters>({})
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "submitted_at",
    direction: "DESC",
  })

  useEffect(() => {
    fetchClaims()
  }, [])

  const fetchClaims = async (
    page = pagination.page,
    status = statusFilter,
    search = searchTerm,
    limitValue?: number,
    filters = advancedFilters,
    sort = sortConfig,
  ) => {
    try {
      setLoading(true)
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const params: any = {
        page: page,
        limit: effectiveLimit,
        ...filters
      }

      if (status) params.status = status
      if (search) params.search = search
      if (sort?.field) {
        params.sort_by = sort.field
        params.sort_order = sort.direction
      }

      const response = await axiosInstance.get("/claims/project/list", { params })

      if (response.data.success) {
        setClaims(response.data.data.claims)
        setPagination(response.data.data.pagination)
      }
    } catch (error) {
      console.error("Error fetching project claims:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (search: string) => {
    setSearchTerm(search)
    // API call triggered by debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchClaims(1, statusFilter, debouncedSearch, undefined, advancedFilters, sortConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const handleFilterChange = (filter: string) => {
    setStatusFilter(filter)
    fetchClaims(1, filter, searchTerm, undefined, advancedFilters, sortConfig)
  }

  const handleAdvancedFilter = (filters: ProjectClaimFilters) => {
    setAdvancedFilters(filters)
    fetchClaims(1, statusFilter, searchTerm, undefined, filters, sortConfig)
  }

  const handlePageChange = (page: number) => {
    fetchClaims(page, statusFilter, searchTerm, undefined, advancedFilters, sortConfig)
  }

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchClaims(1, statusFilter, searchTerm, limit, advancedFilters, sortConfig)
  }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchClaims(pagination.page, statusFilter, searchTerm, undefined, advancedFilters, sort)
  }

  return (
    <div>
      <ProjectClaimsList
        claims={claims}
        pagination={pagination}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        statusFilter={statusFilter}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onAdvancedFilter={handleAdvancedFilter}
        loading={loading}
        sortConfig={sortConfig}
        onSort={handleSort}
      />
    </div>
  )
}

export default ProjectClaim
